export type ColorScheme = "light" | "dark";

/** Loose theme-params bag; the SDK may deliver snake_case or camelCase keys. */
export type ThemeParams = Record<string, string | undefined>;

/** Read a theme value tolerating both `bg_color` and `bgColor` spellings. */
function pick(theme: ThemeParams, snake: string, camel: string): string | undefined {
  return theme[snake] ?? theme[camel];
}

/** Perceived-luminance test so we can pick a scheme from a background color. */
export function isColorDark(hex: string): boolean {
  const m = /^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i.exec(hex.trim());
  if (!m) return false;
  const r = parseInt(m[1] ?? "0", 16);
  const g = parseInt(m[2] ?? "0", 16);
  const b = parseInt(m[3] ?? "0", 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.5;
}

/**
 * Map Telegram theme params onto our design-token CSS variables. Pure (returns
 * a plain record) so it is unit-testable; `applyTheme` writes them to the DOM.
 */
export function themeToCssVars(theme: ThemeParams, scheme: ColorScheme): Record<string, string> {
  const vars: Record<string, string> = {};
  const set = (name: string, value: string | undefined) => {
    if (value) vars[name] = value;
  };
  set("--bg", pick(theme, "bg_color", "bgColor"));
  set("--surface", pick(theme, "secondary_bg_color", "secondaryBgColor"));
  set("--text", pick(theme, "text_color", "textColor"));
  set("--hint", pick(theme, "hint_color", "hintColor"));
  set("--link", pick(theme, "link_color", "linkColor"));
  set("--accent", pick(theme, "button_color", "buttonColor"));
  set("--accent-text", pick(theme, "button_text_color", "buttonTextColor"));
  vars["--color-scheme"] = scheme;
  return vars;
}

export function applyTheme(vars: Record<string, string>): void {
  const root = document.documentElement;
  for (const [name, value] of Object.entries(vars)) {
    root.style.setProperty(name, value);
  }
  // NOTE: the scheme→<html data-theme> attribute is owned by settings/prefs.ts
  // (the «پوسته» preference resolves Telegram's scheme vs. the user override);
  // this helper only publishes CSS variables.
}
